/**
 * Plain text in, note HTML out.
 *
 * Every note model stores an HTML string — the desktop composer is Quill, and
 * `components/Note` renders the field through `html-react-parser`. The field
 * composer is a plain `<textarea>` (Quill's contenteditable is where iOS
 * dictation misbehaves), so its text has to be turned into the same shape the
 * rest of the app already reads.
 *
 * `<p>` per paragraph specifically, because `utils/noteImages.ts` regex-parses
 * `<p><img ...></p>` out of this same string. Storing bare text would round-trip
 * fine today and break the moment a photo is attached.
 */

/**
 * Escape before interpolating. Not optional.
 *
 * The result is rendered as HTML by `html-react-parser`, so an unescaped
 * apostrophe in "Tenant's frontage" is cosmetic but an unescaped `<` is a
 * script-injection vector — and a field note is user input from a phone.
 * `&` first, or the escapes escape each other.
 */
export const escapeHtml = (text: string): string =>
	text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')

/**
 * Text as the user typed it → the note body.
 *
 * A blank line starts a new paragraph; a single newline is a `<br>` inside the
 * current one. That is what a textarea's Return key means to the person
 * pressing it, and it matches what Quill produces for the same keystrokes, so a
 * note written in the field and one written at a desk render identically.
 *
 * Returns '' for empty or whitespace-only input rather than `<p></p>`, so a
 * caller can treat "nothing was written" as falsy.
 */
export const textToNoteHtml = (text: string | null | undefined): string => {
	if (!text) return ''
	const trimmed = text.trim()
	if (!trimmed) return ''

	return trimmed
		.replace(/\r\n/g, '\n')
		// Two or more newlines separate paragraphs; runs of three or more do not
		// create empty ones.
		.split(/\n{2,}/)
		.map(block => block.trim())
		.filter(Boolean)
		.map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
		.join('')
}

/**
 * Append images to a note body, in the shape the rest of the app expects.
 *
 * `<p><img src="..."></p>` — one per paragraph, matching what the desktop
 * composer and the upload wizard both produce, which is what lets
 * `removeImageFromNoteHtml` drop the whole paragraph instead of leaving an
 * empty one behind.
 *
 * A note that is only photographs is legitimate and common in the field, so an
 * empty body is fine.
 */
export const appendImagesToNoteHtml = (
	html: string,
	urls: (string | null | undefined)[],
): string => {
	const tags = urls
		.filter((url): url is string => Boolean(url && url.trim()))
		.map(url => `<p><img src="${escapeHtml(url.trim())}"></p>`)
		.join('')
	return `${html || ''}${tags}`
}
